import { createRegistry, fromBinary, fromJson, Message, Registry } from '@bufbuild/protobuf';
import { GenMessage } from '@bufbuild/protobuf/codegenv2';
import { AnySchema, anyUnpack } from '@bufbuild/protobuf/wkt';
import FROM_JSON_CONFIG from 'api/protobuf/constant/from-json-config';
import { SseEvent } from 'services/sse/SseClient';
import { useMemo } from 'react';
import useServerSentEvents, { UseServerSentEventsResult } from './useServerSentEvents';

export interface DecodedSseEvent {
    /** SSE event name (the `event:` field, e.g. `subscription-event`). */
    event: string;
    /** Fully-qualified protobuf type name (e.g. `events.v1.SubscriptionEvent`). */
    typeName: string;
    /**
     * The decoded, typed protobuf message. `undefined` only in Any mode when the
     * type_url is not in the supplied `schemas` registry — inspect `typeName`/`raw`.
     */
    message: Message | undefined;
    /** Raw `data:` payload, always present (debugging and unregistered types). */
    raw: string;
}

interface CommonOptions {
    /** Path appended to the base URL, e.g. `/sse/v1/subscription-events`. */
    path: string;
    onMessage: (event: DecodedSseEvent) => void;
    onError?: (error: unknown) => void;
    onOpen?: (response: Response) => void;
    enabled?: boolean;
    baseUrl?: string;
}

/** Direct mode: every payload is the same concrete message type (no Any wrapper). */
export interface DirectModeOptions extends CommonOptions {
    schema: GenMessage<Message>;
}

/** Any mode: every payload is a `google.protobuf.Any`, unpacked via the registry. */
export interface AnyModeOptions extends CommonOptions {
    /**
     * Message schemas whose `Any` payloads should be unpacked. Pass a stable
     * (module-level) array so the registry isn't rebuilt on every render.
     */
    schemas: GenMessage<Message>[];
}

export type UseProtobufServerSentEventsOptions = DirectModeOptions | AnyModeOptions;

const isAnyMode = (options: UseProtobufServerSentEventsOptions): options is AnyModeOptions => 'schemas' in options;

const base64ToBytes = (b64: string): Uint8Array => {
    const binary = atob(b64.trim());
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

const isJsonPayload = (data: string): boolean => {
    const head = data.trimStart()[0];
    return head === '{' || head === '[';
};

/**
 * Decodes one SSE `data:` payload into a concrete protobuf message, auto-detecting
 * the wire encoding (proto-JSON when the payload is a JSON object, otherwise base64
 * binary — mirroring `ProtobufClient`'s JSON/binary duality).
 */
const decodeDirect = (data: string, schema: GenMessage<Message>): Message => {
    return isJsonPayload(data)
        ? fromJson(schema, JSON.parse(data), FROM_JSON_CONFIG)
        : fromBinary(schema, base64ToBytes(data));
};

/** Decodes one SSE `data:` payload into a `google.protobuf.Any`. */
const decodeAny = (data: string, registry: Registry) => {
    return isJsonPayload(data)
        ? fromJson(AnySchema, JSON.parse(data), { ...FROM_JSON_CONFIG, registry })
        : fromBinary(AnySchema, base64ToBytes(data));
};

/**
 * SSE hook for streams whose `data:` payload is a protobuf message. Wraps
 * {@link useServerSentEvents} and decodes each event, handing the consumer a
 * typed message in a stable `{ event, typeName, message, raw }` shape.
 *
 * - `{ schema }` (direct mode): every payload is that concrete type.
 * - `{ schemas }` (Any mode): every payload is a `google.protobuf.Any`, resolved
 *   against a registry built from `schemas` and routed by `typeName`.
 */
export const useProtobufServerSentEvents = (options: UseProtobufServerSentEventsOptions): UseServerSentEventsResult => {
    const { path, onMessage, onError, onOpen, enabled, baseUrl } = options;

    const schemas = isAnyMode(options) ? options.schemas : null;
    const registry = useMemo(() => (schemas ? createRegistry(...schemas) : null), [schemas]);

    const handleMessage = (raw: SseEvent) => {
        try {
            if (registry) {
                const any = decodeAny(raw.data, registry);
                const typeName = any.typeUrl.replace(/^.*\//, '');
                onMessage({ event: raw.event, typeName, message: anyUnpack(any, registry), raw: raw.data });
            } else {
                const schema = (options as DirectModeOptions).schema;
                onMessage({
                    event: raw.event,
                    typeName: schema.typeName,
                    message: decodeDirect(raw.data, schema),
                    raw: raw.data,
                });
            }
        } catch (error) {
            // Surface the failure, but still deliver the raw payload so the consumer can react.
            onError?.(error);
            onMessage({ event: raw.event, typeName: '', message: undefined, raw: raw.data });
        }
    };

    return useServerSentEvents({
        path,
        enabled,
        baseUrl,
        onOpen,
        onError,
        onMessage: handleMessage,
    });
};

export default useProtobufServerSentEvents;
